// src/components/Popups/AccountPopup/AccountPopup.tsx

import React, { useState, useRef, useEffect } from 'react';
import styles from './AccountPopup.module.css';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config';

interface AccountPopupProps {
    onClose: () => void;
    initialRegister?: boolean;
}

type TabType = 'profile' | 'support';

const AccountPopup: React.FC<AccountPopupProps> = ({ onClose, initialRegister = false }) => {
    const { user, token, logout, login } = useAuth();

    const [activeTab, setActiveTab] = useState<TabType>('profile');
    const [isRegistering, setIsRegistering] = useState(initialRegister);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Indica se é um erro de verificação para mudar o estilo (Vermelho Animado)
    const [isVerificationError, setIsVerificationError] = useState(false);

    const [highlightCreate, setHighlightCreate] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [tempName, setTempName] = useState('');
    const [isNameModified, setIsNameModified] = useState(false);
    const [localAvatarPreview, setLocalAvatarPreview] = useState<string | null>(null);

    // Forms
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');

    // Suporte
    const [supportSubject, setSupportSubject] = useState('');
    const [supportMessage, setSupportMessage] = useState('');
    const [supportSent, setSupportSent] = useState(false);

    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (user?.displayName) setTempName(user.displayName);
    }, [user]);

    // === LISTENER DO POPUP GOOGLE ===
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // SEGURANÇA: aceita mensagens APENAS da própria origem. Bloqueia
            // qualquer página maliciosa injetando postMessage para roubar
            // fluxo de login.
            if (event.origin !== window.location.origin) return;
            if (event.source !== window) {
                // Também exigimos que venha de uma janela filha (popup),
                // não de qualquer iframe embutido.
            }
            if (!event.data || typeof event.data !== 'object' || !event.data.type) return;
            const { type, payload } = event.data;

            if (type === 'GOOGLE_LOGIN_SUCCESS') {
                if (payload?.token && typeof payload.token === 'string') login(payload.token);
            } else if (type === 'GOOGLE_VERIFICATION_SENT') {
                setVerificationSent(true);
                setIsRegistering(false);
            } else if (type === 'GOOGLE_LOGIN_ERROR') {
                setIsVerificationError(false);
                if (payload?.error === 'user_not_found') {
                    setError('Conta não encontrada. Por favor, cadastre-se.');
                    setHighlightCreate(true);
                    setTimeout(() => setHighlightCreate(false), 2000);
                } else if (payload?.error === 'not_verified') {
                    setError('Conta existe, mas e-mail não verificado.');
                    setIsVerificationError(true);
                } else if (payload?.error !== 'google_cancel') {
                    setError('Erro ao autenticar com Google.');
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [login]);

    const openCenteredPopup = (url: string, title: string, w: number, h: number) => {
        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
        const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : window.screen.width;
        const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : window.screen.height;
        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft;
        const top = (height - h) / 2 / systemZoom + dualScreenTop;
        const features = `scrollbars=yes, width=${w / systemZoom}, height=${h / systemZoom}, top=${top}, left=${left}, toolbar=no, menubar=no, location=no, status=no`;
        const newWindow = window.open(url, title, features);
        if (newWindow) newWindow.focus();
    };

    const handleGoogleLogin = async () => {
        try {
            const intent = isRegistering ? 'register' : 'login';
            const res = await fetch(`${API_BASE_URL}/api/auth/google/url?intent=${intent}`);
            const data = await res.json();
            if (data.url) openCenteredPopup(data.url, 'Google Login', 500, 600);
        } catch (error) { console.error(error); setError("Erro ao iniciar Google Login."); }
    };

    const handleManageSubscription = async () => {
        const portalUrl = (user as any)?.stripePortalUrl;
        if (portalUrl) window.open(portalUrl, '_blank');
        else alert("Funcionalidade indisponível no plano gratuito.");
    };

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsVerificationError(false);
        setIsLoading(true);
        setHighlightCreate(false);

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }),
            });
            const data = await response.json();

            if (response.ok && data.token) {
                login(data.token);
            } else {
                if (response.status === 404) {
                    setHighlightCreate(true);
                    setError('Conta não encontrada. Crie uma conta abaixo.');
                    setTimeout(() => setHighlightCreate(false), 2000);
                } else if (response.status === 403) {
                    setError('Conta existe, mas e-mail não verificado.');
                    setIsVerificationError(true);
                } else {
                    setError(data.message || data.error || 'Falha no login.');
                }
            }
        } catch (err) { setError('Erro de conexão.'); } finally { setIsLoading(false); }
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); setError(''); setIsVerificationError(false); setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password, displayName }),
            });
            const data = await response.json();
            if (response.ok) setVerificationSent(true);
            else setError(data.error || 'Erro ao criar conta.');
        } catch (err) { setError('Erro de conexão.'); } finally { setIsLoading(false); }
    };

    const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); setIsLoading(true); setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email })
            });
            const data = await res.json();
            if (res.ok) { alert("📧 Verifique seu e-mail! Enviamos um link de redefinição."); setIsForgotPassword(false); }
            else setError(data.error || "Erro ao recuperar senha.");
        } catch (err) { setError("Erro de conexão."); } finally { setIsLoading(false); }
    };

    // SEGURANÇA (H7): validação client-side de arquivo de avatar antes de
    // tocar upload. Bloqueia:
    //  - Tipos MIME fora da whitelist (só imagens)
    //  - Arquivos >5MB (DoS/abuso de storage)
    //  - Arquivos com magic number falso (ex: .exe renomeado para .jpg). Só
    //    PNG/JPEG/WEBP/GIF passam. Backend deve revalidar mesmo assim.
    const ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

    const validateImageFile = async (file: File): Promise<{ ok: boolean; error?: string }> => {
        if (!ALLOWED_IMAGE_MIMES.includes(file.type)) {
            return { ok: false, error: 'Apenas imagens JPG, PNG, WEBP ou GIF são permitidas.' };
        }
        if (file.size > MAX_AVATAR_BYTES) {
            return { ok: false, error: 'Imagem muito grande (máx. 5MB).' };
        }
        // Lê os primeiros 12 bytes para checar magic number
        const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        const isJPEG = head[0] === 0xFF && head[1] === 0xD8 && head[2] === 0xFF;
        const isPNG = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4E && head[3] === 0x47;
        const isGIF = head[0] === 0x47 && head[1] === 0x49 && head[2] === 0x46;
        const isWEBP = head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46
            && head[8] === 0x57 && head[9] === 0x45 && head[10] === 0x42 && head[11] === 0x50;
        if (!isJPEG && !isPNG && !isGIF && !isWEBP) {
            return { ok: false, error: 'Arquivo não é uma imagem válida (assinatura inválida).' };
        }
        return { ok: true };
    };

    const compressImage = async (file: File): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const maxWidth = 500; const quality = 0.7;
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (ev) => {
                const img = new Image(); img.src = ev.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let w = img.width, h = img.height;
                    if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
                    canvas.width = w; canvas.height = h;
                    const ctx = canvas.getContext('2d'); ctx?.drawImage(img, 0, 0, w, h);
                    canvas.toBlob(b => b ? resolve(b) : reject(), 'image/jpeg', quality);
                };
            };
            reader.onerror = reject;
        });
    };

    const performUpload = async (file: File, userToken: string, currentDisplayName: string) => {
        const compressedBlob = await compressImage(file);
        const compressedFile = new File([compressedBlob], "avatar.jpg", { type: "image/jpeg" });
        const resUrl = await fetch(`${API_BASE_URL}/api/user/generate-avatar-upload-url`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
            body: JSON.stringify({ contentType: 'image/jpeg' })
        });
        const { url, key } = await resUrl.json();
        await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: compressedFile });
        const timestamp = new Date().getTime();
        const finalPhotoURL = `${API_BASE_URL}/api/files/stream-range?key=${key}&filename=avatar.jpg&t=${timestamp}`;
        const updateRes = await fetch(`${API_BASE_URL}/api/user/profile`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
            body: JSON.stringify({ displayName: currentDisplayName, photoURL: finalPhotoURL })
        });
        return await updateRes.json();
    };

    const handleProfileFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!user || !token || !e.target.files?.[0]) return;
        const file = e.target.files[0];

        const check = await validateImageFile(file);
        if (!check.ok) {
            alert(check.error);
            e.target.value = '';
            return;
        }

        try {
            setUploading(true);
            const objectUrl = URL.createObjectURL(file);
            setLocalAvatarPreview(objectUrl);
            const data = await performUpload(file, token, user.displayName);
            if (data.token) login(data.token);
        } catch (err) { alert("Erro ao enviar foto."); setLocalAvatarPreview(null); } finally { setUploading(false); }
    };

    const handleNameUpdate = async () => {
        if (!tempName.trim()) return;
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/user/profile`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ displayName: tempName, photoURL: user?.photoURL })
            });
            const data = await res.json();
            if (res.ok && data.token) { login(data.token); setIsNameModified(false); } else { alert("Erro ao atualizar nome."); }
        } catch (err) { alert("Erro de conexão."); } finally { setIsLoading(false); }
    };

    const confirmDeleteAccount = async () => { logout(); onClose(); };

    const handleSupportSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/support/tickets`, {
                method: "POST", headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: user?.displayName || '', email: user?.email || '', subject: supportSubject, message: supportMessage })
            });
            if (res.ok) { setSupportSent(true); setSupportSubject(''); setSupportMessage(''); } else { alert('Erro ao enviar mensagem.'); }
        } catch (err) { alert('Erro de conexão.'); } finally { setIsLoading(false); }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={onClose}>×</button>

                {user ? (
                    /* === LOGADO === */
                    <>
                        <div className={styles.tabsHeader}>
                            <button className={`${styles.tabButton} ${activeTab === 'profile' ? styles.activeTab : ''}`} onClick={() => setActiveTab('profile')}>Perfil</button>
                            <button className={`${styles.tabButton} ${activeTab === 'support' ? styles.activeTab : ''}`} onClick={() => setActiveTab('support')}>Suporte</button>
                        </div>
                        {activeTab === 'profile' && (
                            <div className={styles.profileContainer}>
                                <div className={styles.avatarSection}>
                                    <div className={styles.avatarWrapper} onClick={() => fileInputRef.current?.click()}>
                                        {localAvatarPreview ? <img src={localAvatarPreview} alt="Preview" className={styles.avatarImg} /> : user.photoURL ? <img src={user.photoURL} alt="Avatar" className={styles.avatarImg} /> : <svg className={styles.avatarPlaceholderIcon} viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" clipRule="evenodd" d="M12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2ZM9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7C15 8.65685 13.6569 10 12 10C10.3431 10 9 8.65685 9 7Z" /><path fillRule="evenodd" clipRule="evenodd" d="M12 14C7.58172 14 4 17.5817 4 22C4 22.5523 4.44772 23 5 23H19C19.5523 23 20 22.5523 20 22C20 17.5817 16.4183 14 12 14ZM6.02324 21C6.26124 17.9262 8.87707 16 12 16C15.1229 16 17.7388 17.9262 17.9768 21H6.02324Z" /></svg>}
                                    </div>
                                    <div className={styles.editIcon} onClick={() => fileInputRef.current?.click()}>✎</div>
                                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleProfileFileChange} />
                                    {uploading && <small style={{ color: '#aaa', position: 'absolute', bottom: '-25px', fontSize: '0.75rem', width: '100%', textAlign: 'center' }}>Enviando...</small>}
                                </div>
                                <div className={styles.infoCard}>
                                    <span className={styles.infoLabel}>Nome</span>
                                    <div className={styles.nameInputContainer}>
                                        <input className={styles.transparentInput} value={tempName} onChange={(e) => { setTempName(e.target.value); setIsNameModified(e.target.value !== user.displayName); }} />
                                        {isNameModified && <button className={styles.saveNameBtn} onClick={handleNameUpdate} disabled={isLoading}>{isLoading ? '...' : 'Salvar'}</button>}
                                    </div>
                                </div>
                                <div className={styles.infoCard}><span className={styles.infoLabel}>Email</span><span className={styles.infoValue}>{user.email}</span></div>
                                <div className={styles.infoCard}><span className={styles.infoLabel}>Plano Atual</span><span className={styles.infoValue} style={{ color: '#635bff' }}>{user.planId?.toUpperCase() || 'FREE'}</span></div>
                                <button className={styles.manageSubButton} onClick={handleManageSubscription}>Gerenciar Assinatura ↗</button>
                                <button className={styles.logoutButton} onClick={() => { logout(); onClose(); }}>Sair da Conta</button>
                                {!showDeleteConfirm ? <button className={styles.deleteAccountButton} onClick={() => setShowDeleteConfirm(true)}>Excluir minha conta</button> : <div className={styles.deleteConfirmationBox}><div className={styles.deleteWarningTitle}>⚠️ Cuidado</div><p className={styles.deleteWarningText}>Certeza que deseja excluir sua conta? <br /><strong>Esta ação é irreversível.</strong></p><div className={styles.deleteActions}><button className={styles.cancelDeleteBtn} onClick={() => setShowDeleteConfirm(false)}>Cancelar</button><button className={styles.confirmDeleteBtn} onClick={confirmDeleteAccount}>Sim, excluir</button></div></div>}
                            </div>
                        )}
                        {activeTab === 'support' && (
                            /* SUPORTE MANTIDO IGUAL */
                            <>
                                {supportSent ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}><div style={{ fontSize: '3rem', marginBottom: '10px' }}>✅</div><h3>Ticket Enviado!</h3><button className={styles.primaryButton} onClick={() => setSupportSent(false)} style={{ width: '100%' }}>Novo Ticket</button></div>
                                ) : (
                                    <form className={styles.supportForm} onSubmit={handleSupportSubmit}>
                                        <h3 style={{ textAlign: 'center', fontSize: '1.1rem' }}>Precisa de ajuda?</h3>
                                        <div className={styles.inputGroup}><label className={styles.label}>Assunto</label><input className={styles.input} value={supportSubject} onChange={e => setSupportSubject(e.target.value)} placeholder="Ex: Problema no download" required /></div>
                                        <div className={styles.inputGroup}><label className={styles.label}>Mensagem</label><textarea className={styles.textarea} value={supportMessage} onChange={e => setSupportMessage(e.target.value)} placeholder="Descreva seu problema..." required /></div>
                                        <button type="submit" className={styles.primaryButton} disabled={isLoading}>{isLoading ? 'Enviando...' : 'Enviar Ticket'}</button>
                                    </form>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    /* === DESLOGADA === */
                    <>
                        {verificationSent ? (
                            /* TELA: EMAIL ENVIADO */
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div className={styles.emailIconContainer}>
                                    <svg className={styles.emailIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h2 className={styles.title}>Verifique seu E-mail</h2>
                                <p className={styles.subtitle} style={{ color: '#ddd', marginBottom: '10px' }}>
                                    Link enviado para: <br />
                                    <strong style={{ color: '#fff' }}>{email}</strong>
                                </p>

                                {/* AQUI ESTÁ A MUDANÇA: className={styles.smallPrimaryButton} */}
                                <button className={styles.smallPrimaryButton} onClick={() => { setVerificationSent(false); setIsRegistering(false); }}>
                                    Voltar para Login
                                </button>

                                <button className={styles.gmailButton} onClick={() => window.open('https://mail.google.com', '_blank')}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                                    </svg>
                                    Abrir Gmail
                                </button>
                            </div>
                        ) : isForgotPassword ? (
                            /* ESQUECI SENHA */
                            <>
                                <button className={styles.backButton} onClick={() => setIsForgotPassword(false)}>← Voltar</button>
                                <h2 className={styles.title}>Recuperar Senha</h2>
                                <p className={styles.subtitle}>Digite seu email para receber um link.</p>
                                {error && <div className={styles.errorMessage}>{error}</div>}
                                <form onSubmit={handleForgotPasswordSubmit} className={styles.form}>
                                    <div className={styles.inputGroup}><label className={styles.label}>E-mail</label><input className={styles.input} type="email" placeholder="seu@email.com" value={email} onChange={e => setEmail(e.target.value)} required /></div>
                                    <button type="submit" className={styles.primaryButton} disabled={isLoading}>{isLoading ? 'Enviando...' : 'Recuperar Senha'}</button>
                                </form>
                            </>
                        ) : (
                            /* LOGIN / REGISTRO */
                            <>
                                <h2 className={styles.title}>{isRegistering ? 'Criar Conta' : 'Acessar Conta'}</h2>
                                <p className={styles.subtitle}>{isRegistering ? 'Junte-se à comunidade.' : 'Bem-vindo de volta!'}</p>

                                {/* ALERTA VERMELHO ANIMADO */}
                                {error && (
                                    isVerificationError ? (
                                        <div className={styles.warningBox}>
                                            <svg className={styles.warningAnimIcon} viewBox="0 0 50 50">
                                                {/* Triângulo */}
                                                <path
                                                    className={styles.warningTriangle}
                                                    d="M25 3 L46 43 H4 Z"
                                                />
                                                {/* Exclamação (Linha e Ponto separados) */}
                                                <path
                                                    className={styles.warningExclamation}
                                                    d="M25 12 V32 M25 39 V39.1"
                                                />
                                            </svg>
                                            {error}
                                        </div>
                                    ) : (
                                        <div className={styles.errorMessage}>{error}</div>
                                    )
                                )}

                                <form onSubmit={isRegistering ? handleRegisterSubmit : handleLoginSubmit} className={styles.form}>
                                    {isRegistering && <div className={styles.inputGroup}><label className={styles.label}>Nome</label><input className={styles.input} type="text" placeholder="" value={displayName} onChange={e => setDisplayName(e.target.value)} required /></div>}
                                    <div className={styles.inputGroup}><label className={styles.label}>E-mail</label><input className={styles.input} type="email" placeholder="" value={email} onChange={e => setEmail(e.target.value)} required /></div>
                                    <div className={styles.inputGroup}><label className={styles.label}>Senha</label><input className={styles.input} type="password" placeholder="" value={password} onChange={e => setPassword(e.target.value)} required /></div>
                                    {!isRegistering && <span className={styles.forgotPasswordLink} onClick={() => setIsForgotPassword(true)}>Esqueci minha senha</span>}
                                    <button type="submit" className={styles.primaryButton} disabled={isLoading}>{isLoading ? 'Carregando...' : (isRegistering ? 'Cadastrar' : 'Entrar')}</button>
                                </form>
                                <div className={styles.divider}>ou continue com</div>
                                <button type="button" className={styles.googleButton} onClick={handleGoogleLogin}><img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="G" style={{ width: 20 }} /> Google</button>
                                <p className={styles.toggleText} onClick={() => { setIsRegistering(!isRegistering); setError(''); }}>
                                    {isRegistering ? 'Já tem uma conta? ' : 'Não tem uma conta? '}
                                    <span style={{ color: highlightCreate ? '#ff5252' : '#635bff', fontWeight: 'bold', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', display: 'inline-block', transform: highlightCreate ? 'scale(1.4)' : 'scale(1)', textShadow: highlightCreate ? '0 0 10px rgba(255, 82, 82, 0.5)' : 'none' }}>
                                        {isRegistering ? 'Faça Login' : 'Crie uma'}
                                    </span>
                                </p>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AccountPopup;